/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import classNames from 'classnames';
import _ from 'lodash';
import Q from 'q';

// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import transloadit from 'js/lib/transloadit';

import { Button, VisuallyHidden } from '@coursera/cds-core';

import type { Assembly } from 'bundles/authoring/common/types/Upload';
import constants from 'bundles/phoenix/constants/fileUploaderConstants';

import _t from 'i18n!nls/phoenix';

type FileUploaderFormProps = {
  templateId: string;
  onReset: () => void;
  fields?: any;
  accept?: string;
  buttonContent?: React.ReactNode;
  buttonClasses?: Array<string>;
  buttonCss?: $TSFixMe; // ?: Interpolation<Theme> gives an error when running yarn tsc -p static/bundles/author-common
  onUploadSelect?: () => void;
  onUploadStart?: () => void;
  onUploadProgress?: (x0: any) => void;
  onUploadComplete?: (x0: any, x1: Assembly) => void;
  onUploadResult?: (x0: any, x1: any) => void;
  onUploadSuccess?: (x0: Assembly) => void;
  onUploadError?: (x0: any) => void;
  onUploadCancel?: () => void;
  disabled?: boolean;
  onFocus?: (x0?: any) => void;
  onBlur?: (x0?: any) => void;
  hideFormWhileUploading?: boolean;
  showCancelLink?: boolean;
  children?: JSX.Element;
  id?: string;
};

type FileUploaderFormState = {
  uploadState: 'ready' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  isFocused: boolean;
};

const styles = {
  progressContainer: css`
    .bt3-hide {
      display: none !important;
    }

    .bt3-progress {
      overflow: hidden;
      height: 20px;
      margin-bottom: 20px;
      background-color: #f5f5f5;
      border-radius: 4px;
      -webkit-box-shadow: inset 0 1px 2px rgba(0 0 0 / 10%);
      box-shadow: inset 0 1px 2px rgba(0 0 0 / 10%);
    }

    .bt3-progress-bar {
      float: left;
      width: 0%;
      height: 100%;
      font-size: 12px;
      line-height: 20px;
      color: #fff;
      text-align: center;
      background-color: #428bca;
      -webkit-box-shadow: inset 0 -1px 0 rgba(0 0 0 / 15%);
      box-shadow: inset 0 -1px 0 rgba(0 0 0 / 15%);
      -webkit-transition: width 0.6s ease;
      transition: width 0.6s ease;
    }
  `,
  button: (theme: Theme) =>
    css({
      marginTop: 'var(--cds-spacing-150)',
      padding: 'var(--cds-spacing-100) var(--cds-spacing-200)',
      fontSize: '14px',
      fontFamily: `Source Sans Pro,Arial,sans-serif`,
      fontWeight: '700',
      borderRadius: 'var(--cds-spacing-50)',
      textTransform: 'none',
      textAlign: 'center',
      maxWidth: '320px',
      minWidth: 'unset',
      transition: 'none',
      color: 'var(--cds-color-white-0)',
      borderColor: 'var(--cds-color-blue-700)',
      backgroundColor: 'var(--cds-color-blue-700)',
      boxShadow: `inset 0 0 0 1px var(--cds-color-blue-700)`,
      '&:hover': {
        borderColor: 'var(--cds-color-blue-800)',
        backgroundColor: 'var(--cds-color-blue-800)',
        color: 'var(--cds-color-white-0)',
      },
      '&:active': {
        borderColor: 'var(--cds-color-purple-950)',
        backgroundColor: 'var(--cds-color-purple-950)',
        '&.disableElevation': {
          '&::after': {
            content: 'unset',
          },
        },
      },

      '&.disabled': {
        color: 'var(--cds-color-grey-50)',
        background: 'var(--cds-color-grey-200)',
        boxShadow: 'none',
        cursor: 'not-allowed',
      },
    }),

  input: css({
    position: 'absolute',
    left: '-100000px',
    width: '0.1px',
    height: '0.1px',
    opacity: 0,
    overflow: 'hidden',
    zIndex: '-1',

    '&:hover': {
      cursor: 'pointer',
    },
  }),

  progressBar: (theme: Theme) =>
    css({
      backgroundColor: 'var(--cds-color-blue-800)',
    }),

  uploadCancel: css({
    float: 'right',
  }),
};

class FileUploaderForm extends React.Component<FileUploaderFormProps, FileUploaderFormState> {
  declare uniqueId: string;

  static defaultProps = {
    hideFormWhileUploading: true,
    buttonClasses: [],
    accept: '',
  };

  state: FileUploaderFormState = {
    uploadState: 'ready',
    progress: 0,
    isFocused: false,
  };

  constructor(props: FileUploaderFormProps) {
    super(props);
    this.uniqueId = _.uniqueId();
  }

  componentDidMount() {
    this.createUploader();
  }

  componentWillUnmount() {
    this.removeUploader();
  }

  handleCancel = () => {
    this.uploader.cancel();
    this.onCancel();
    this.reset();
  };

  onUploadComplete = (uploads: any, assembly: Assembly) => {
    const { onUploadComplete } = this.props;
    if (onUploadComplete) {
      onUploadComplete(uploads, assembly);
    }
  };

  onResult = (step: any, result: any) => {
    const { onUploadResult } = this.props;
    if (onUploadResult) {
      onUploadResult(step, result);
    }
  };

  onSuccess = (assembly: Assembly) => {
    if (assembly.ok && !_.isEmpty(assembly.results)) {
      const { onUploadSuccess } = this.props;
      this.setState(() => ({
        uploadState: 'uploaded',
        progress: 100,
      }));

      if (onUploadSuccess) {
        Q.fcall(onUploadSuccess, assembly).then(this.reset).done();
      } else {
        this.reset();
      }
    } else {
      this.onError(new Error('Unknown upload error'));
    }
  };

  onError = (error: any) => {
    const { onUploadError } = this.props;
    const { uploadState } = this.state;

    if (uploadState === 'uploading') {
      this.uploader.cancel();
    }

    this.setState(() => ({
      uploadState: 'error',
      progress: 0,
    }));

    if (onUploadError) {
      onUploadError(error);
    }

    this.reset();
  };

  onSelect = () => {
    const { onUploadSelect } = this.props;
    this.setState(() => ({
      uploadState: 'uploading',
      progress: 0,
    }));

    if (onUploadSelect) {
      onUploadSelect();
    }
  };

  onStart = () => {
    const { onUploadStart } = this.props;
    if (onUploadStart) {
      onUploadStart();
    }
  };

  onProgress = (assembly: Assembly) => {
    const { onUploadProgress } = this.props;
    const received = assembly.bytes_received;
    const expected = assembly.bytes_expected;
    const progress = expected === 0 ? 0 : (100 * received) / expected;

    this.setState(() => ({
      progress,
    }));

    if (onUploadProgress) {
      onUploadProgress(progress);
    }
  };

  onCancel = () => {
    const { onUploadCancel } = this.props;
    if (onUploadCancel) {
      onUploadCancel();
    }
  };

  onFocus = (...args: any) => {
    const { onFocus } = this.props;

    this.setState(() => ({ isFocused: true }));
    if (onFocus) {
      onFocus(...args);
    }
  };

  onBlur = (...args: any) => {
    const { onBlur } = this.props;
    this.setState(() => ({ isFocused: false }));
    if (onBlur) {
      onBlur(...args);
    }
  };

  reset = () => {
    const { onReset } = this.props;
    this.removeUploader();
    onReset();
  };

  uploader: any = null;

  uploaderRef: any = null;

  createUploader() {
    const { templateId, fields } = this.props;
    const transloaditParams = {
      params: {
        auth: {
          key: constants.transloaditKey,
        },
        // eslint-disable-next-line camelcase
        template_id: templateId,
        fields,
      },
    };

    this.uploader = transloadit(this.uploaderRef, transloaditParams);
    this.uploader.on('select', this.onSelect);
    this.uploader.on('start', this.onStart);
    this.uploader.on('progress', this.onProgress);
    this.uploader.on('upload-complete', this.onUploadComplete);
    this.uploader.on('result', this.onResult);
    this.uploader.on('success', this.onSuccess);
    this.uploader.on('error', this.onError);
    this.uploader.on('cancel', this.onCancel);
  }

  removeUploader() {
    if (!this.uploader) {
      return;
    }

    this.uploader.reset();
    this.uploader.off('select', this.onSelect);
    this.uploader.off('start', this.onStart);
    this.uploader.off('progress', this.onProgress);
    this.uploader.off('upload-complete', this.onUploadComplete);
    this.uploader.off('result', this.onResult);
    this.uploader.off('success', this.onSuccess);
    this.uploader.off('error', this.onError);
    this.uploader.off('cancel', this.onCancel);
    this.uploader = null;
  }

  renderUploadingState() {
    const { showCancelLink } = this.props;
    const { uploadState, progress } = this.state;
    if (uploadState === 'uploading') {
      const currentProgress = progress || 0;
      const fakeProgress = Math.min(Math.max(currentProgress, 5), 95).toFixed(2);
      const style = {
        width: fakeProgress + '%',
      };

      return (
        <div className="uploaded">
          <div className="bt3-progress">
            <VisuallyHidden role="alert" aria-live="polite" component="span" className="rc-A11yScreenReaderOnly">
              {_t('Uploading')}
            </VisuallyHidden>
            <div className="bt3-progress-bar" css={styles.progressBar} style={style} />
          </div>
          {showCancelLink && (
            <Button variant="ghost" size="small" onClick={this.handleCancel} css={styles.uploadCancel}>
              {_t('Cancel')}
            </Button>
          )}
        </div>
      );
    }
    return null;
  }

  renderUploadedState() {
    const { uploadState } = this.state;

    if (uploadState === 'uploaded') {
      return (
        <div role="alert" className="uploaded">
          {_t('Uploaded')}
        </div>
      );
    }
    return null;
  }

  render() {
    const { hideFormWhileUploading, buttonClasses, accept, disabled, children, buttonContent, id, buttonCss } =
      this.props;
    const { uploadState, isFocused } = this.state;

    const classes = classNames('rc-FileUploaderForm', {
      uploading: uploadState === 'uploading',
    });

    const buttonClassNames = classNames(buttonClasses, isFocused ? 'focus' : '', disabled ? 'disabled' : '');

    const formClasses = classNames({
      'bt3-hide': hideFormWhileUploading && uploadState !== 'ready',
    });

    const inputId = `upload-file-input-${this.uniqueId}`;

    // TODO(wbowers): Render errors.

    return (
      <div css={styles.progressContainer}>
        <div className={classes}>
          <form
            className={formClasses}
            ref={(uploader) => {
              this.uploaderRef = uploader;
            }}
          >
            <input
              className="custom-file-input"
              type="file"
              name="file"
              multiple={false}
              css={styles.input}
              accept={accept}
              disabled={disabled}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              id={inputId}
            />
            <label
              id={id || `upload-file-input-${this.uniqueId}-label`}
              htmlFor={inputId}
              className={buttonClassNames}
              css={[styles.button, !!buttonCss && buttonCss]}
            >
              {children || buttonContent}
            </label>
          </form>
          {this.renderUploadingState()}
          {this.renderUploadedState()}
        </div>
      </div>
    );
  }
}

type FileUploaderProps = {
  templateId: string;
  fields?: any;
  buttonContent?: React.ReactNode;
  buttonClasses?: Array<string>;
  buttonCss?: Interpolation<Theme>;
  showCancelLink?: boolean;
  onReset?: () => void;
  onUploadSelect?: () => void;
  onUploadStart?: () => void;
  onUploadProgress?: (x0: any) => void;
  onUploadComplete?: (x0: any, x1: Assembly) => void;
  onUploadResult?: (x0: any, x1: any) => void;
  onUploadSuccess?: (x0: Assembly) => void;
  onUploadError?: (x0: any) => void;
  onUploadCancel?: () => void;
  disabled?: boolean;
  accept?: string;
  className?: string;
  hideFormWhileUploading?: boolean;
  onFocus?: (x0?: any) => void;
  onBlur?: (x0?: any) => void;
  children?: JSX.Element;
};

const FileUploader = (props: FileUploaderProps) => {
  const { onReset, className } = props;
  const classes = classNames('rc-FileUploader', className);
  const [key, setKey] = useState(1);

  /**
   * Change a key to re-mount the component with transloadit transloadit requires re-initialization to function properly
   * @see https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
   */
  const handleReset = () => {
    setKey((prev) => prev + 1);
    onReset?.();
  };
  return (
    <div className={classes}>
      <FileUploaderForm key={key} {...props} onReset={handleReset} />
    </div>
  );
};

export default FileUploader;
